import { Col, Form, Modal, Select, message, Typography, Space } from 'antd'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import CustomRow from '../../../reusable-components/CustomRow'
import { handleError } from '../../../lib/utils'
import { readClinics } from '../../clinics/utils/utils'
import AgiliteSkeleton from '../../../reusable-components/AgiliteSkeleton'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClinicMedical, faStethoscope, faHeadSideCough } from '@fortawesome/free-solid-svg-icons'

const { Title, Text } = Typography

const BeginExaminationModal = ({ examinationModalOpen, setExaminationModalOpen, beginAdhocExamination }) => {
  const authState = useSelector((state) => state.auth)
  const checkinState = useSelector((state) => state.auth?.config?.checkin || [])
  const [data, setData] = useState(null)
  const [examinationForm] = Form.useForm()
  const [clinics, setClinics] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    handleSetClinics()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSetClinics = async () => {
    setLoading(true)
    let clinics = []
    try {
      clinics = await readClinics({ _id: { $in: authState.agiliteUser.extraData.clinics } })
      setClinics(clinics)
    } catch (e) {
      message.error(handleError(e, true))
    }
    setLoading(false)
  }

  const handleFinish = async (data) => {
    setLoading(true)
    try {
      if (authState.agiliteUser.extraData.clinics.length === 1) {
        data.clinic = JSON.stringify(clinics[0])
      }

      // Ensure patientSelection is set even if we don't collect symptoms
      if (!data.patientSelection) {
        data.patientSelection = [data.chiefComplaint]
      }

      await beginAdhocExamination(data)
    } catch (e) {
      message.error(handleError(e, true))
    }
    setLoading(false)
  }

  // Define common chief complaints
  const commonChiefComplaints = [
    { label: 'Flu-like symptoms', value: 'Flu-like symptoms' },
    { label: 'Follow-up visit', value: 'Follow-up visit' },
    { label: 'Chronic condition management', value: 'Chronic condition management' },
    { label: 'Acute pain', value: 'Acute pain' },
    { label: 'Respiratory issues', value: 'Respiratory issues' },
    { label: 'Gastrointestinal problems', value: 'Gastrointestinal problems' },
    { label: 'Skin condition', value: 'Skin condition' },
    { label: 'Mental health concern', value: 'Mental health concern' },
    { label: 'Medication review', value: 'Medication review' },
    { label: 'Other', value: 'Other' }
  ]

  // Combine with existing checkin options if available
  const chiefComplaintOptions =
    checkinState.length > 0
      ? checkinState.map((option) => ({
          label: option.questionText,
          value: option.doctorsText
        }))
      : commonChiefComplaints

  return (
    <Modal
      title={
        <Space align='center'>
          <FontAwesomeIcon icon={faStethoscope} style={{ color: '#1c3faa', fontSize: '18px' }} />
          <Title level={4} style={{ margin: 0 }}>
            New Consultation
          </Title>
        </Space>
      }
      onCancel={() => {
        setExaminationModalOpen(false)
      }}
      maskClosable={false}
      footer={null}
      destroyOnClose={true}
      open={examinationModalOpen}
      width={500}
      bodyStyle={{ padding: '24px' }}
    >
      <Form
        layout='vertical'
        form={examinationForm}
        onFieldsChange={() => {
          setData(examinationForm.getFieldsValue())
        }}
        onFinish={(data) => {
          handleFinish(data)
        }}
      >
        <CustomRow justify='end'>
          {loading ? (
            <Col span={24}>
              <AgiliteSkeleton skActive spinnerTip='Loading...' />
            </Col>
          ) : (
            <>
              <Col span={24} style={{ marginTop: '16px', marginBottom: '12px' }}>
                <div
                  style={{
                    padding: '12px 16px',
                    borderRadius: '8px',
                    background: '#f0f8ff',
                    border: '1px solid #d1ecf1',
                    color: '#0c5460'
                  }}
                >
                  <strong>Note:</strong> This will create a new booking record for walk-in consultation, preparing the
                  patient for immediate clinical assessment and documentation.
                </div>
              </Col>

              <Col span={24} style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
                <button
                  onClick={() => {
                    setData(null)
                    examinationForm.resetFields()
                    setExaminationModalOpen(false)
                  }}
                  type='button'
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: '1px solid #d9d9d9',
                    background: '#f5f5f5',
                    color: '#595959',
                    cursor: 'pointer',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  disabled={loading}
                >
                  Cancel
                </button>

                <button
                  onClick={() => {
                    examinationForm.submit()
                  }}
                  type='button'
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: '2px solid #1c8a5e',
                    background: '#f6fff9',
                    color: '#1c8a5e',
                    cursor: 'pointer',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 4px rgba(28, 138, 94, 0.1)'
                  }}
                  disabled={loading}
                >
                  Begin Consultation
                </button>
              </Col>
            </>
          )}
        </CustomRow>
      </Form>
    </Modal>
  )
}

export default BeginExaminationModal
